import React, { forwardRef, useRef, useState, useImperativeHandle, } from 'react';
import { Box, styled } from '@mui/material';
import clsx from 'clsx';
import omit from 'lodash-es/omit';
import RcInput from 'rc-input';
import { inputClasses, getStyle, inputClassPrefix } from './style';
import { useUniqueId } from '../../hooks/useUniqueId';
import { triggerAriaMessage } from '../../Shared/a11yUtils';
import ClearButton from './ClearButton';
import { useTranslation } from '../../hooks/useTranslation';
function parseInternalProps(props) {
    const result = { ...props };
    if (props.defaultValue === null) {
        result.defaultValue = '';
    }
    if (props.value === null) {
        result.value = '';
    }
    return result;
}
const InputBase = forwardRef((props, ref) => {
    const internalProps = parseInternalProps(props);
    const { sx, prefix, suffix, addonBefore, addonAfter, className, allowClear, status, showCount, maxLength, onChange, onBlur, onFocus, onKeyDown, disabled, readOnly, inputRef, handlerRef, 'aria-describedby': ariaDescribedby, ...rest } = internalProps;
    const { t } = useTranslation('Input');
    const id = useUniqueId('input');
    const [focused, setFocused] = useState(false);
    const rcInputRef = useRef(null);
    useImperativeHandle(inputRef, () => rcInputRef.current?.input, []);
    useImperativeHandle(handlerRef, () => {
        const instance = {
            blur: () => {
                rcInputRef.current?.blur();
            },
            focus: () => {
                rcInputRef.current?.focus();
            },
            select: () => {
                rcInputRef.current?.select();
            },
            setSelectionRange: (start, end, direction) => {
                rcInputRef.current?.setSelectionRange(start, end, direction);
            },
        };
        return instance;
    }, []);
    let mergedAllowClear;
    if (typeof allowClear === 'object') {
        mergedAllowClear = allowClear;
    }
    else if (allowClear) {
        mergedAllowClear = {
            clearIcon: (React.createElement(ClearButton, { className: clsx({
                    [`${inputClasses.clearIconHasSuffix}`]: !!suffix || showCount,
                }) })),
        };
    }
    const handleBlur = (e) => {
        setFocused(false);
        onBlur?.(e);
    };
    const handleFocus = (e) => {
        setFocused(true);
        onFocus?.(e);
    };
    const handleChange = (e) => {
        onChange?.(e);
    };
    const handleKeyDown = (e) => {
        onKeyDown?.(e);
        if (maxLength &&
            !readOnly &&
            !disabled &&
            !e.ctrlKey &&
            !e.metaKey &&
            (e.nativeEvent.isComposing || e.key.length === 1)) {
            const { currentTarget } = e;
            setTimeout(() => {
                if (currentTarget.value.length >= maxLength) {
                    triggerAriaMessage(t('maxLengthMessage', { maxLength }));
                }
            }, 0);
        }
    };
    const prefixDescription = typeof addonBefore === 'string'
        ? t('prefix', { prefix: addonBefore })
        : undefined;
    const suffixDescription = typeof addonAfter === 'string'
        ? t('suffix', { suffix: addonAfter })
        : undefined;
    const affixDescription = prefixDescription && suffixDescription
        ? t('prefixAndSuffix', {
            prefix: addonBefore,
            suffix: addonAfter,
        })
        : (prefixDescription ?? suffixDescription);
    const describedby = ariaDescribedby ?? (affixDescription ? id('desc') : undefined);
    return (React.createElement(Box, { ref: ref, className: clsx(className, inputClasses.root, { [`${inputClasses.rootError}`]: status === 'error' }, { [`${inputClasses.rootWarning}`]: status === 'warning' }, { [`${inputClasses.disabled}`]: disabled }, { [`${inputClasses.readOnly}`]: readOnly }), sx: sx },
        React.createElement(RcInput, { ...rest, addonAfter: addonAfter, addonBefore: addonBefore, onBlur: handleBlur, onFocus: handleFocus, onKeyDown: handleKeyDown, prefix: prefix, suffix: suffix, allowClear: mergedAllowClear, prefixCls: inputClassPrefix, onChange: handleChange, showCount: showCount, maxLength: maxLength, disabled: disabled, readOnly: readOnly, "aria-describedby": describedby, "aria-invalid": status === 'error', classNames: {
                input: clsx(inputClasses.input, { [`${inputClasses.inputDisabled}`]: disabled }, { [`${inputClasses.inputReadOnly}`]: readOnly }),
            }, classes: {
                group: clsx({ [`${inputClasses.groupFocused}`]: focused }, { [`${inputClasses.groupDisabled}`]: disabled }),
            }, ref: rcInputRef }),
        affixDescription ? (React.createElement("div", { id: id('desc'), style: { display: 'none' } }, affixDescription)) : null));
});
const StyledInput = styled(InputBase)((props) => {
    const { theme, disabled, readOnly, status, prefix, suffix, allowClear, size, showCount, } = props;
    const shouldRenderAffix = !!prefix || !!suffix || !!allowClear || !!showCount;
    return getStyle(theme, {
        disabled,
        readOnly,
        status,
        size,
        shouldRenderAffix,
    });
});
const RefInput = React.forwardRef((props, ref) => (React.createElement(StyledInput, { ...props, ref: ref })));
//  The exposed components must support the use of ref.
const InternalInput = React.forwardRef((props, ref) => {
    const internalProps = omit(props, ['style']);
    return React.createElement(RefInput, { ...internalProps, ref: ref });
});
export default InternalInput;
